import { forwardRef, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Printer, ClipboardList, FileSpreadsheet, Loader as Loader2 } from 'lucide-react';
import { useCompany } from '@/context/CompanyContext';
import {
  exportOrdersToExcel, exportReturnsToExcel, fmtDocDate, orderFileBase, orderTotals, returnFileBase,
  type PrintableOrder, type PrintableReturn,
} from '@/lib/incomingDocsExport';

type OrderMode = 'full' | 'picking';

const fmtMoney = (n: number) =>
  `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PAGE_STYLE = `
  @page { size: A4; margin: 12mm; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .print-page { break-after: page; }
    .print-page:last-child { break-after: auto; }
    tr { break-inside: avoid; }
  }
`;

const th = 'border border-gray-300 bg-gray-100 px-2 py-1.5 text-right text-[11px] font-semibold text-gray-700';
const td = 'border border-gray-300 px-2 py-1.5 text-[11px] text-gray-900 align-middle';

function DocHeader({ companyName, title, number, date, status, client }: {
  companyName: string;
  title:       string;
  number:      string;
  date:        string;
  status:      string;
  client:      { name: string; phone?: string | null; email?: string | null };
}) {
  return (
    <div className="mb-4">
      <div className="flex items-start justify-between border-b-2 border-gray-800 pb-3 mb-3">
        <div>
          <p className="text-lg font-bold text-gray-900">{companyName}</p>
          <p className="text-sm text-gray-600 mt-1">{title}</p>
        </div>
        <div className="text-left text-xs text-gray-700 space-y-0.5">
          <p><span className="font-semibold">מס׳:</span> <span dir="ltr">{number}</span></p>
          <p><span className="font-semibold">תאריך:</span> {fmtDocDate(date)}</p>
          <p><span className="font-semibold">סטטוס:</span> {status}</p>
        </div>
      </div>
      <div className="text-xs text-gray-800 flex flex-wrap gap-x-6 gap-y-1">
        <p><span className="font-semibold">לקוח:</span> {client.name}</p>
        {client.phone && <p><span className="font-semibold">טלפון:</span> <span dir="ltr">{client.phone}</span></p>}
        {client.email && <p><span className="font-semibold">אימייל:</span> <span dir="ltr">{client.email}</span></p>}
      </div>
    </div>
  );
}

function Thumb({ url }: { url: string | null }) {
  return url
    ? <img src={url} alt="" className="w-9 h-9 object-cover rounded" />
    : <div className="w-9 h-9 rounded bg-gray-100" />;
}

export const IncomingOrdersPrintView = forwardRef<HTMLDivElement, {
  orders:      PrintableOrder[];
  mode:        OrderMode;
  companyName: string;
  title:       string;
}>(function IncomingOrdersPrintView({ orders, mode, companyName, title }, ref) {
  return (
    <div ref={ref} dir="rtl" className="bg-white text-gray-900" style={{ fontFamily: 'Arial, sans-serif' }}>
      {orders.map(order => {
        const withPrices = mode === 'full';
        const lines = withPrices
          ? order.lines
          : [...order.lines].sort((a, b) => a.sku.localeCompare(b.sku, 'he'));
        const totals = orderTotals(order);
        const showDiscount = withPrices && totals.discount > 0;
        const totalApproved = order.lines.reduce((s, l) => s + l.approved, 0);
        const totalRequested = order.lines.reduce((s, l) => s + l.requested, 0);
        return (
          <section key={order.id} className="print-page p-2">
            <DocHeader
              companyName={companyName}
              title={withPrices ? title : `${title} - רשימת ליקוט`}
              number={order.shortId}
              date={order.createdAt}
              status={order.statusLabel}
              client={{ name: order.clientName, phone: order.clientPhone, email: order.clientEmail }}
            />
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {!withPrices && <th className={`${th} w-8`}>✓</th>}
                  <th className={`${th} w-8`}>#</th>
                  <th className={`${th} w-12`}>תמונה</th>
                  <th className={th}>מוצר</th>
                  <th className={th}>מק״ט</th>
                  <th className={`${th} text-center`}>הוזמן</th>
                  <th className={`${th} text-center`}>מאושר</th>
                  {order.isOpen && <th className={`${th} text-center w-20`}>נלקט בפועל</th>}
                  {withPrices && <th className={`${th} text-left`}>מחיר ליח׳</th>}
                  {showDiscount && <th className={`${th} text-left`}>הנחה</th>}
                  {withPrices && <th className={`${th} text-left`}>סה״כ</th>}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={`${l.sku}-${i}`}>
                    {!withPrices && <td className={td}><div className="w-4 h-4 border border-gray-500 mx-auto" /></td>}
                    <td className={`${td} text-center`}>{i + 1}</td>
                    <td className={td}><Thumb url={l.imageUrl} /></td>
                    <td className={`${td} font-medium`}>{l.name}</td>
                    <td className={`${td} font-mono`} dir="ltr">{l.sku}</td>
                    <td className={`${td} text-center tabular-nums`}>{l.requested}</td>
                    <td className={`${td} text-center tabular-nums font-semibold`}>{l.approved}</td>
                    {order.isOpen && <td className={td} />}
                    {withPrices && <td className={`${td} text-left tabular-nums`}>{fmtMoney(l.unitPrice)}</td>}
                    {showDiscount && <td className={`${td} text-left tabular-nums`}>{l.discount ? fmtMoney(l.discount) : ''}</td>}
                    {withPrices && <td className={`${td} text-left tabular-nums`}>{fmtMoney(l.lineTotal)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 flex justify-between items-start gap-6 text-xs">
              <div className="space-y-1 text-gray-700">
                <p>סה״כ שורות: <span className="font-semibold">{order.lines.length}</span></p>
                <p>סה״כ יחידות שהוזמנו: <span className="font-semibold">{totalRequested}</span></p>
                <p>סה״כ יחידות מאושרות: <span className="font-semibold">{totalApproved}</span></p>
                {withPrices && order.discountReason && (
                  <p>סיבת הנחה: <span className="font-semibold">{order.discountReason}</span></p>
                )}
              </div>
              {withPrices ? (
                <table className="border-collapse min-w-[220px]">
                  <tbody>
                    {showDiscount && (
                      <>
                        <tr><td className={td}>סה״כ לפני הנחה</td><td className={`${td} text-left tabular-nums`}>{fmtMoney(totals.gross)}</td></tr>
                        <tr><td className={td}>הנחה</td><td className={`${td} text-left tabular-nums`}>-{fmtMoney(totals.discount)}</td></tr>
                      </>
                    )}
                    <tr><td className={td}>סה״כ לפני מע״מ</td><td className={`${td} text-left tabular-nums`}>{fmtMoney(totals.subtotal)}</td></tr>
                    <tr><td className={td}>מע״מ ({order.vatRate}%)</td><td className={`${td} text-left tabular-nums`}>{fmtMoney(totals.vat)}</td></tr>
                    <tr><td className={`${td} font-bold`}>סה״כ כולל מע״מ</td><td className={`${td} text-left tabular-nums font-bold`}>{fmtMoney(totals.total)}</td></tr>
                  </tbody>
                </table>
              ) : (
                <div className="space-y-4 text-gray-700 min-w-[220px]">
                  <p>שם המלקט: ______________________</p>
                  <p>חתימה: _________________________</p>
                </div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
});

export const IncomingReturnsPrintView = forwardRef<HTMLDivElement, {
  returns:     PrintableReturn[];
  companyName: string;
  title:       string;
}>(function IncomingReturnsPrintView({ returns, companyName, title }, ref) {
  return (
    <div ref={ref} dir="rtl" className="bg-white text-gray-900" style={{ fontFamily: 'Arial, sans-serif' }}>
      {returns.map(r => (
        <section key={r.id} className="print-page p-2">
          <DocHeader
            companyName={companyName}
            title={title}
            number={r.shortId}
            date={r.createdAt}
            status={r.statusLabel}
            client={{ name: r.clientName }}
          />
          {r.reason && (
            <p className="text-xs mb-3"><span className="font-semibold">סיבת ההחזרה:</span> {r.reason}</p>
          )}
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={`${th} w-8`}>#</th>
                <th className={th}>מוצר</th>
                <th className={th}>מק״ט</th>
                <th className={`${th} text-center`}>כמות מבוקשת</th>
                <th className={`${th} text-center w-20`}>{r.isOpen ? 'התקבל בפועל' : 'התקבל'}</th>
                <th className={`${th} text-left`}>מחיר ליח׳</th>
                <th className={th}>סיבה</th>
              </tr>
            </thead>
            <tbody>
              {r.lines.map((l, i) => (
                <tr key={`${l.sku}-${i}`}>
                  <td className={`${td} text-center`}>{i + 1}</td>
                  <td className={`${td} font-medium`}>{l.name}</td>
                  <td className={`${td} font-mono`} dir="ltr">{l.sku}</td>
                  <td className={`${td} text-center tabular-nums`}>{l.requested}</td>
                  <td className={`${td} text-center tabular-nums font-semibold`}>
                    {r.isOpen ? '' : (l.received ?? '')}
                  </td>
                  <td className={`${td} text-left tabular-nums`}>{fmtMoney(l.unitPrice)}</td>
                  <td className={td}>{l.reason ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 flex justify-between items-start gap-6 text-xs text-gray-700">
            <div className="space-y-1">
              <p>סה״כ שורות: <span className="font-semibold">{r.lines.length}</span></p>
              <p>סה״כ יחידות מבוקשות: <span className="font-semibold">{r.lines.reduce((s, l) => s + l.requested, 0)}</span></p>
              {r.adminNotes && <p>הערות: {r.adminNotes}</p>}
            </div>
            <div className="space-y-4 min-w-[220px]">
              <p>נבדק על ידי: ______________________</p>
              <p>חתימה: _________________________</p>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
});

const btnBase = `inline-flex items-center justify-center gap-1.5 rounded-lg border text-xs font-semibold
  transition-colors active:scale-[.98] disabled:opacity-40 disabled:cursor-not-allowed`;
const btnSize = 'px-3 py-1.5';

function useCompanyName() {
  const { activeCompany } = useCompany();
  return activeCompany?.legal_name ?? '';
}

export function OrderPrintActions({ orders, title, filePrefix }: {
  orders:     PrintableOrder[];
  title:      string;
  filePrefix: string;
}) {
  const companyName = useCompanyName();
  const ref = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<OrderMode | null>(null);
  const fileBase = orderFileBase(orders, filePrefix);
  const print = useReactToPrint({
    contentRef:    ref,
    documentTitle: fileBase,
    pageStyle:     PAGE_STYLE,
    onAfterPrint:  () => setMode(null),
  });

  useEffect(() => {
    if (mode) print();
  }, [mode, print]);

  const disabled = orders.length === 0;
  return (
    <>
      <button type="button" disabled={disabled || !!mode} onClick={() => setMode('full')}
        className={`${btnBase} ${btnSize} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}>
        {mode === 'full' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
        הזמנה מלאה עם מחירים
      </button>
      <button type="button" disabled={disabled || !!mode} onClick={() => setMode('picking')}
        className={`${btnBase} ${btnSize} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}>
        {mode === 'picking' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
        רשימת ליקוט ללא מחירים
      </button>
      <button type="button" disabled={disabled} onClick={() => exportOrdersToExcel(orders, fileBase)}
        className={`${btnBase} ${btnSize} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}>
        <FileSpreadsheet className="w-3.5 h-3.5" />
        הורד אקסל
      </button>
      {mode && (
        <div className="hidden">
          <IncomingOrdersPrintView ref={ref} orders={orders} mode={mode} companyName={companyName} title={title} />
        </div>
      )}
    </>
  );
}

export function ReturnPrintActions({ returns, title, filePrefix }: {
  returns:    PrintableReturn[];
  title:      string;
  filePrefix: string;
}) {
  const companyName = useCompanyName();
  const ref = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);
  const fileBase = returnFileBase(returns, filePrefix);
  const print = useReactToPrint({
    contentRef:    ref,
    documentTitle: fileBase,
    pageStyle:     PAGE_STYLE,
    onAfterPrint:  () => setPrinting(false),
  });

  useEffect(() => {
    if (printing) print();
  }, [printing, print]);

  const disabled = returns.length === 0;
  return (
    <>
      <button type="button" disabled={disabled || printing} onClick={() => setPrinting(true)}
        className={`${btnBase} ${btnSize} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}>
        {printing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
        הדפס / PDF
      </button>
      <button type="button" disabled={disabled} onClick={() => exportReturnsToExcel(returns, fileBase)}
        className={`${btnBase} ${btnSize} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}>
        <FileSpreadsheet className="w-3.5 h-3.5" />
        הורד אקסל
      </button>
      {printing && (
        <div className="hidden">
          <IncomingReturnsPrintView ref={ref} returns={returns} companyName={companyName} title={title} />
        </div>
      )}
    </>
  );
}

export function SelectionBar({ count, onClear, children }: {
  count:    number;
  onClear:  () => void;
  children: React.ReactNode;
}) {
  if (count === 0) return null;
  return (
    <div className="sticky top-2 z-10 flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-xl
                    bg-gray-900 text-white shadow-lg">
      <span className="text-sm font-semibold">{count} נבחרו</span>
      <button type="button" onClick={onClear}
        className="text-xs text-gray-300 hover:text-white underline-offset-2 hover:underline ml-auto">
        נקה בחירה
      </button>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export function RowCheckbox({ checked, onChange, label }: {
  checked:  boolean;
  onChange: () => void;
  label:    string;
}) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={onChange}
      onClick={e => e.stopPropagation()}
      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-300 cursor-pointer"
    />
  );
}
